import { useEffect, useMemo, useRef, useState } from 'react';
import { ContentWithLegendProps } from '@/components';
import { LegendMode } from '@/core';
import { TimeSeriesChartProps } from '../time-series-chart-model';

export interface UseTimeSeriesQuickFilterProps extends Pick<TimeSeriesChartProps, 'showToggleMode' | 'onChangeMode'> {
  initMode: LegendMode;
}

export interface UseTimeSeriesQuickFilterResponse {
  toggleModeProps: Required<Required<ContentWithLegendProps>['toggleModeProps']>;
}

/**
 * Hook to help with use of the "toggle mode" feature in the time series chart.
 */
export const useTimeSeriesToggleMode = ({
  showToggleMode,
  initMode,
  onChangeMode,
}: UseTimeSeriesQuickFilterProps): UseTimeSeriesQuickFilterResponse => {
  const [mode, setMode] = useState<LegendMode>(initMode);
  const prevInitMode = useRef(initMode);

  // Make sure that the mode gets adjusted if the passed in props change. This is important for
  // ensuring the mode can be changed in dashboard settings.
  useEffect(() => {
    if (initMode && initMode !== prevInitMode.current) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMode(initMode);
      prevInitMode.current = initMode;
    }
  }, [initMode]);

  const toggleModeProps: UseTimeSeriesQuickFilterResponse['toggleModeProps'] = useMemo(() => {
    return {
      show: !!showToggleMode,
      onChange: (newMode: LegendMode) => {
        setMode(newMode);
        onChangeMode?.(newMode);
      },
      mode,
    };
  }, [mode, onChangeMode, showToggleMode]);

  return {
    toggleModeProps,
  };
};
